import React, { useEffect, useState } from "react";
import { getArticles, getAds } from "../lib/api";
import { FeatureArticle, ArticleCard, SectionHeading } from "../components/ArticleCard";
import { AdSlot, LeaderboardAd, NativeAd } from "../components/Ads";
import { DailyDeal, PetOfWeek, UpcomingEvents } from "../components/HomeWidgets";
import SocialFeed from "../components/SocialFeed";
import BreakingTicker from "../components/BreakingTicker";
import { Link } from "react-router-dom";
import { Waves } from "lucide-react";

const SECTIONS = [
  { slug: "news", title: "Local News", tagline: "Amelia Island · Fernandina · Yulee" },
  { slug: "sports", title: "High School Sports", tagline: "Go Pirates" },
  { slug: "entertainment", title: "Entertainment", tagline: "Arts · Music · Food" },
  { slug: "columnists", title: "Columnists", tagline: "Island voices" },
];

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [ads, setAds] = useState([]);

  useEffect(() => {
    getArticles({ limit: 100 }).then(setArticles).catch(() => {});
    getAds().then(setAds).catch(() => {});
  }, []);

  const byCat = (c) => articles.filter((a) => a.category === c);
  const lead = articles.find((a) => a.featured && a.category === "news") || articles[0];
  const secondary = articles.filter((a) => a.slug !== lead?.slug).slice(0, 4);
  const leaderboard = ads.find((a) => a.slot === "leaderboard");
  const sidebarAds = ads.filter((a) => a.slot === "sidebar");
  const nativeAd = ads.find((a) => a.slot === "native");

  if (!lead) return <div className="mx-auto max-w-7xl px-4 py-20 text-center font-ui text-slate-500">Loading today's edition…</div>;

  return (
    <div className="paper-texture">
      <BreakingTicker articles={articles} />
      <Link to="/" className="block bg-[var(--navy)] py-2 text-center font-ui text-xs font-semibold uppercase tracking-wider text-white hover:bg-[var(--header-navy)]" data-testid="try-portal-v2-link">
        ✦ Go to Nassau Life — our community portal →
      </Link>
      <div className="mx-auto max-w-7xl px-4 py-4">{leaderboard && <LeaderboardAd ad={leaderboard} />}</div>

      {/* Broadsheet hero */}
      <div className="mx-auto grid max-w-7xl gap-8 px-4 lg:grid-cols-12">
        <div className="order-2 lg:order-1 lg:col-span-3">
          <h3 className="section-rule font-display pt-2 text-lg font-bold text-[var(--navy)]">More Local Headlines</h3>
          <div className="mt-4 space-y-5">
            {secondary.map((a) => <ArticleCard key={a.slug} article={a} horizontal />)}
          </div>
        </div>

        <div className="order-1 lg:order-2 lg:col-span-6">
          <FeatureArticle article={lead} />
        </div>

        <aside className="order-3 lg:col-span-3">
          <div className="sticky top-40 space-y-6">
            <div className="rounded-lg border border-[var(--paper-border)] bg-white p-4">
              <div className="flex items-center gap-2 text-[var(--navy)]">
                <Waves size={18} className="text-[var(--gold)]" />
                <h4 className="font-ui text-sm font-bold uppercase tracking-wider">Tides & Weather</h4>
              </div>
              <div className="font-serif-body mt-3 space-y-2 text-sm text-slate-700">
                <p className="flex justify-between"><span>High Tide</span><b>7:42 AM · 6:11 PM</b></p>
                <p className="flex justify-between"><span>Low Tide</span><b>1:18 PM · 12:55 AM</b></p>
                <p className="flex justify-between"><span>Today</span><b>78°F · Sunny</b></p>
                <p className="flex justify-between"><span>Water Temp</span><b>74°F</b></p>
              </div>
            </div>
            <PetOfWeek />
            <UpcomingEvents />
            <DailyDeal />
            {sidebarAds[0] && <AdSlot ad={sidebarAds[0]} className="rounded-lg" />}
          </div>
        </aside>
      </div>

      {/* Native ad */}
      {nativeAd && (
        <div className="mx-auto max-w-7xl px-4 py-10">
          <NativeAd ad={nativeAd} />
        </div>
      )}

      {/* Section blocks */}
      <div className="mx-auto max-w-7xl px-4 pb-10">
        {SECTIONS.map((sec, idx) => {
          const list = byCat(sec.slug).slice(0, 3);
          if (list.length === 0) return null;
          return (
            <section key={sec.slug} className="mb-14">
              <SectionHeading title={sec.title} tagline={sec.tagline} to={`/section/${sec.slug}`} sectionKey={sec.slug} />
              <div className="grid gap-8 md:grid-cols-3">
                {list.map((a) => <ArticleCard key={a.slug} article={a} />)}
              </div>
              {idx === 0 && sidebarAds[1] && (
                <div className="mt-8"><AdSlot ad={sidebarAds[1]} className="mx-auto max-w-md rounded-lg" /></div>
              )}
            </section>
          );
        })}

        {/* Social + Directory teaser */}
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeading title="From Our Community" tagline="Follow the island conversation" />
            <SocialFeed />
          </div>
          <div>
            <SectionHeading title="Local Business" tagline="Directory" to="/directory" />
            <div className="rounded-lg border border-[var(--paper-border)] bg-[var(--navy)] p-6 text-white">
              <h4 className="font-display text-xl font-bold">Discover Amelia Island's best local businesses</h4>
              <p className="font-serif-body mt-2 text-sm text-white/80">
                From riverfront dining to marinas and real estate — explore our always-free community directory with sponsored and premium listings.
              </p>
              <Link to="/directory" className="font-ui mt-4 inline-block rounded-full bg-[var(--gold)] px-5 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#b08f48]">
                Browse Directory
              </Link>
            </div>
            <div className="mt-6">{sidebarAds[0] && <AdSlot ad={sidebarAds[0]} className="rounded-lg" />}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
